import { DEFAULT_MODEL_NAMES } from "./constants";

import { Parameter, ComponentType, Model } from "@/queries/models/types";
import { PromptBlueprint, SchemaDefinition } from "@/types";

export type ParameterValue =
  | string
  | number
  | boolean
  | string[]
  | { type: string; json_schema: SchemaDefinition }
  | Record<string, string | number | boolean>
  | { [key: string]: ParameterValue }
  | null;

export type ParametersObject = Record<string, ParameterValue>;

export const getPreviewModelName = (promptData?: Partial<PromptBlueprint>) => {
  const modelName = promptData?.metadata?.model?.name;
  if (promptData?.metadata?.model?.provider === "huggingface") {
    return promptData.inference_client_name || modelName || "";
  }

  return modelName || promptData?.inference_client_name || "";
};

export const isNonNumericStringDefault = (value: ParameterValue | undefined): boolean => {
  if (typeof value !== "string") {
    return false;
  }

  const trimmed = value.trim();

  if (trimmed === "") {
    return false;
  }

  return Number.isNaN(Number(trimmed));
};

export const isInputParameterNumeric = (
  defaultValue: ParameterValue | undefined,
  min: ParameterValue | undefined,
  max: ParameterValue | undefined,
): boolean => {
  if (min === undefined && max === undefined) {
    return false;
  }

  if (isNonNumericStringDefault(defaultValue)) {
    return false;
  }

  return true;
};

export const convertToNumber = (value: ParameterValue) => {
  if (typeof value === "string" && !isNaN(Number(value))) {
    return Number(value);
  }

  return value;
};

export const isBoolean = (value: ParameterValue) =>
  typeof value === "string" && (value === "true" || value === "false");

const isJsonSchemaResponseFormat = (param: Parameter): boolean => {
  const value = param.value ?? param.default;
  return (
    param.param_id === "response_format" &&
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    (value as { type?: string }).type === "json_schema"
  );
};

const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }

  if (typeof a === "object" && a !== null && typeof b === "object" && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  return String(a) === String(b);
};

/** Matches dropdown option values, including JSON schema objects with extra fields. */
export const optionMatchesDropdownValue = (
  optionValue: ParameterValue,
  selected: ParameterValue | undefined,
): boolean => {
  if (selected === undefined) {
    return false;
  }

  if (optionValue === selected) {
    return true;
  }

  if (optionValue === null && selected === null) {
    return true;
  }

  if (
    typeof optionValue === "object" &&
    optionValue !== null &&
    typeof selected === "object" &&
    selected !== null &&
    !Array.isArray(selected) &&
    "type" in optionValue &&
    "type" in selected &&
    (optionValue as { type?: string }).type === "json_schema" &&
    (selected as { type?: string }).type === "json_schema"
  ) {
    return true;
  }

  return valuesEqual(optionValue, selected);
};

const isDropdownValueValid = (param: Parameter, value: ParameterValue): boolean => {
  const { options } = param;

  if (!options?.length) {
    return true;
  }

  return options.some(option => optionMatchesDropdownValue(option.value, value));
};

const isNumericInRange = (
  value: ParameterValue,
  min?: number,
  max?: number,
  isInt?: boolean,
): boolean => {
  const num = typeof value === "number" ? value : Number(value);

  if (Number.isNaN(num)) {
    return false;
  }

  if (isInt && !Number.isInteger(num)) {
    return false;
  }

  if (min !== undefined && num < min) {
    return false;
  }

  if (max !== undefined && num > max) {
    return false;
  }

  return true;
};

export const isParameterValueValid = (
  param: Parameter,
  value: ParameterValue | undefined,
): boolean => {
  if (value === undefined) {
    return false;
  }

  switch (param.component_type) {
    case ComponentType.DROPDOWN:
      return isDropdownValueValid(param, value);
    case ComponentType.SLIDER:
      return isNumericInRange(value, param.min, param.max);
    case ComponentType.INPUT:
      if (!isInputParameterNumeric(param.default, param.min, param.max)) {
        return typeof value === "string" || typeof value === "number";
      }

      return isNumericInRange(value, param.min, param.max, param.is_int);
    case ComponentType.MULTI_SELECT: {
      if (!Array.isArray(value)) {
        return false;
      }

      const options = param.options ?? [];

      return value.every(item => options.some(option => valuesEqual(option.value, item)));
    }
    case ComponentType.TAGS:
      return Array.isArray(value) || typeof value === "string";
    case ComponentType.GROUP:
      return typeof value === "object" && value !== null && !Array.isArray(value);
    default:
      return true;
  }
};

const getTemplateDefaultValue = (param: Parameter): ParameterValue => {
  if (param.component_type === ComponentType.GROUP) {
    const templateDefault =
      typeof param.default === "object" && param.default !== null && !Array.isArray(param.default)
        ? (param.default as ParametersObject)
        : undefined;

    return (param.parameters ?? []).reduce((acc: ParametersObject, nested) => {
      const candidate = templateDefault?.[nested.param_id];

      acc[nested.param_id] =
        candidate !== undefined && isParameterValueValid(nested, candidate)
          ? candidate
          : getTemplateDefaultValue(nested);

      return acc;
    }, {} as ParametersObject);
  }

  return param.default ?? param.value;
};

export const resolveParameterValue = (
  param: Parameter,
  previousValue: ParameterValue | undefined,
): ParameterValue => {
  if (param.component_type === ComponentType.GROUP) {
    const previousGroup =
      typeof previousValue === "object" && previousValue !== null && !Array.isArray(previousValue)
        ? (previousValue as ParametersObject)
        : {};

    return (param.parameters ?? []).reduce((acc: ParametersObject, nested) => {
      acc[nested.param_id] = resolveParameterValue(nested, previousGroup[nested.param_id]);

      return acc;
    }, {} as ParametersObject);
  }

  if (previousValue !== undefined && isParameterValueValid(param, previousValue)) {
    return previousValue;
  }

  return getTemplateDefaultValue(param);
};

const processParameter = (param: Parameter): ParameterValue => {
  if (param.component_type === ComponentType.GROUP) {
    return param.parameters?.reduce((acc: ParametersObject, nestedParam: Parameter) => {
      if (!nestedParam.is_disabled) {
        const processedValue = processParameter(nestedParam);

        if (processedValue !== undefined) {
          if (isBoolean(processedValue)) {
            acc[nestedParam.param_id] = processedValue === "true";

            return acc;
          }

          acc[nestedParam.param_id] = processedValue;
        }
      }
      return acc;
    }, {}) as ParameterValue;
  }

  // Never ship stale/invalid dropdown values (e.g. voice=0 after speech catalog changes).
  if (param.component_type === ComponentType.DROPDOWN) {
    return resolveParameterValue(param, param.value);
  }

  if (isBoolean(param.value)) {
    return param.value === "true";
  }

  return convertToNumber(param.value ?? param.default);
};

export const convertToBlueprintModel = (
  promptData: Partial<PromptBlueprint> | undefined,
): Partial<PromptBlueprint> | undefined => {
  if (!promptData?.metadata?.model?.parameters) {
    return promptData;
  }

  const parametersObject = promptData.metadata.model.parameters.reduce(
    (acc: ParametersObject, param: Parameter) => {
      if (!param.is_disabled || isJsonSchemaResponseFormat(param)) {
        const paramId = param.param_id;
        const paramValue = processParameter(param);

        if (paramValue !== undefined) {
          acc[paramId] = paramValue;
        }
      }
      return acc;
    },
    {} as ParametersObject,
  );

  return {
    inference_client_name: promptData.inference_client_name || null,
    provider_base_url_name: promptData.provider_base_url_name || null,
    provider_id: promptData.provider_id || undefined,
    metadata: {
      ...promptData.metadata,
      model: {
        ...promptData.metadata.model,
        parameters: parametersObject,
      },
    },
  };
};

export const mapInitialDataWithExistingParams = (
  initialData?: Partial<PromptBlueprint>,
  existingParams: Parameter[] = [],
): Partial<PromptBlueprint> | undefined => {
  if (!initialData?.metadata?.model) {
    return undefined;
  }

  const initialParamsMap = new Map(Object.entries(initialData.metadata.model.parameters || {}));

  const updatedExistingParams = existingParams.map(param => {
    const paramId = param.param_id;
    const hasMatch = initialParamsMap.has(paramId);
    const previousValue = initialParamsMap.get(paramId);

    initialParamsMap.delete(paramId);

    const resolvedValue = resolveParameterValue(param, hasMatch ? previousValue : undefined);

    if (param.component_type === ComponentType.GROUP && param.parameters) {
      const groupValue = resolvedValue as ParametersObject;

      return {
        ...param,
        is_disabled: hasMatch ? previousValue === undefined && !!param.is_disabled : true,
        value: groupValue,
        parameters: param.parameters.map(nested => ({
          ...nested,
          value: groupValue[nested.param_id],
          is_disabled: false,
        })),
      };
    }

    return {
      ...param,
      is_disabled: hasMatch ? previousValue === undefined && !!param.is_disabled : true,
      value: resolvedValue,
    };
  });

  const customParams: Parameter[] = Array.from(initialParamsMap).map(([key, value]) => ({
    param_id: key,
    name: key,
    value,
    default: null,
    is_disabled: false,
    is_custom: true,
    component_type: ComponentType.INPUT,
  }));

  const combinedParameters = [...updatedExistingParams, ...customParams];

  return {
    ...initialData,
    metadata: {
      ...initialData.metadata,
      model: {
        ...initialData.metadata.model,
        parameters: combinedParameters as Parameter[],
      },
    },
  };
};

const mergeParameterOnSwitch = (
  param: Parameter,
  previousValue: ParameterValue | undefined,
  wasEnabledInSnapshot: boolean,
  wasExplicitlyDisabled: boolean,
): Parameter => {
  const resolvedValue = resolveParameterValue(param, previousValue);
  const resolveDisabled = (): boolean => {
    if (wasExplicitlyDisabled) {
      return true;
    }
    if (wasEnabledInSnapshot) {
      return false;
    }
    return !!param.is_disabled;
  };
  const isDisabled = resolveDisabled();

  if (param.component_type === ComponentType.GROUP && param.parameters) {
    const groupValue = resolvedValue as ParametersObject;

    return {
      ...param,
      value: groupValue,
      is_disabled: isDisabled,
      parameters: param.parameters.map(nested => {
        const nestedParam = {
          ...nested,
          value: groupValue[nested.param_id],
          is_disabled: false,
        };

        return nestedParam as Parameter;
      }),
    };
  }

  return {
    ...param,
    is_disabled: isDisabled,
    value: resolvedValue,
  } as Parameter;
};

export const mergeModelParametersOnSwitch = (
  previous: Partial<PromptBlueprint> | undefined,
  newModelParams: Parameter[],
  disabledParamIds?: Set<string>,
): Parameter[] => {
  const previousParams = previous?.metadata?.model?.parameters;
  const hasPreviousParams =
    previousParams &&
    typeof previousParams === "object" &&
    !Array.isArray(previousParams) &&
    Object.keys(previousParams).length > 0;

  if (!hasPreviousParams && !disabledParamIds?.size) {
    return newModelParams;
  }

  const previousMap = hasPreviousParams ? new Map(Object.entries(previousParams)) : new Map();

  return newModelParams.map(param => {
    const paramId = param.param_id;

    return mergeParameterOnSwitch(
      param,
      previousMap.get(paramId),
      previousMap.has(paramId),
      disabledParamIds?.has(paramId) ?? false,
    );
  });
};

export const getModel = (models: Model[], name: string) =>
  models.find(m => m.display_name === name || m.llm_model_name === name);

export const getModelParams = (apiModels?: Model[], name?: string): Parameter[] => {
  if (!apiModels || !name) {
    return [];
  }

  return getModel(apiModels, name)?.params || [];
};

export const getDefaultModel = (apiModels: Model[], isPromptChat?: boolean) => {
  const provider = "openai";
  const defaultModel = !isPromptChat ? "gpt-3.5-turbo-instruct" : DEFAULT_MODEL_NAMES[provider];
  const defaultParameters = getModelParams(apiModels, defaultModel);

  return {
    name: defaultModel,
    provider,
    parameters: defaultParameters as Parameter[],
  };
};

export const getHuggingFaceModel = (models: Model[], isPromptChat?: boolean) =>
  models?.find(m => m.is_chat === isPromptChat);
